import Clarity from '@microsoft/clarity';
import { appInsights } from '../appInsights';

type ConsentCategory = 'analytics' | 'marketing' | 'functional' | 'necessary';

interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  necessary: boolean;
}

class ConsentManager {
  private consentState: ConsentState = {
    analytics: false,
    marketing: false,
    functional: true,
    necessary: true
  };

  private clarityInitialized = false;
  private appInsightsInitialized = false;
  private listeners: Array<(state: ConsentState) => void> = [];

  constructor() {
    // Check for existing consent state in localStorage
    const savedConsent = localStorage.getItem('cookieConsent');
    if (savedConsent) {
      try {
        this.consentState = JSON.parse(savedConsent);
        this.applyConsent();
      } catch (e) {
        console.error('Failed to parse saved consent', e);
      }
    }

    // Listen for consent manager events from the external script
    this.setupConsentManagerListener();
  }

  private setupConsentManagerListener() {
    // Listen for consent changes from Consent Manager
    if (typeof window !== 'undefined') {
      // Consent Manager uses window.__cmp for communication
      interface CmpWindow extends Window {
        __cmp?: {
          (command: string, parameter: unknown, callback: (result: unknown) => void): void;
          q?: unknown[];
        };
        cmpLoaded?: boolean;
      }
      const cmpWindow = window as CmpWindow;
      cmpWindow.__cmp = cmpWindow.__cmp || function(...args: unknown[]) {
        (cmpWindow.__cmp!.q = cmpWindow.__cmp!.q || []).push(args);
      };

      // Check if consent manager is loaded
      if (cmpWindow.cmpLoaded) {
        this.checkConsentStatus();
      } else {
        // Wait for consent manager to load
        const checkInterval = setInterval(() => {
          if (cmpWindow.cmpLoaded || cmpWindow.__cmp) {
            clearInterval(checkInterval);
            this.checkConsentStatus();
          }
        }, 100);

        // Stop checking after 10 seconds
        setTimeout(() => clearInterval(checkInterval), 10000);
      }
    }
  }

  private checkConsentStatus() {
    // Query consent manager for current consent status
    interface CmpResult {
      purposeConsents?: Record<number, boolean>;
    }
    interface CmpWindow extends Window {
      __cmp?: (command: string, parameter: unknown, callback: (result: CmpResult) => void) => void;
    }
    const cmpWindow = window as CmpWindow;
    if (cmpWindow.__cmp) {
      cmpWindow.__cmp('getVendorConsents', null, (result: CmpResult) => {
        if (result && result.purposeConsents) {
          // Purpose 1: Store and/or access information on a device
          // Purpose 3: Create a personalised ads profile
          // Purpose 5: Create a personalised content profile
          // Purpose 7: Measure ad performance
          // Purpose 8: Measure content performance
          // Purpose 9: Apply market research to generate audience insights

          // Map purposes to our categories
          const hasAnalyticsConsent = result.purposeConsents[7] || result.purposeConsents[8] || result.purposeConsents[9];
          const hasMarketingConsent = result.purposeConsents[3] || result.purposeConsents[5];

          this.updateConsent({
            analytics: hasAnalyticsConsent || false,
            marketing: hasMarketingConsent || false,
            functional: true,
            necessary: true
          });
        }
      });

      // Listen for consent changes
      cmpWindow.__cmp('addEventListener', 'onConsentChanged', (event: CmpResult) => {
        if (event && event.purposeConsents) {
          const hasAnalyticsConsent = event.purposeConsents[7] || event.purposeConsents[8] || event.purposeConsents[9];
          const hasMarketingConsent = event.purposeConsents[3] || event.purposeConsents[5];

          this.updateConsent({
            analytics: hasAnalyticsConsent || false,
            marketing: hasMarketingConsent || false,
            functional: true,
            necessary: true
          });
        }
      });
    }
  }

  public updateConsent(newState: Partial<ConsentState>) {
    const previousState = { ...this.consentState };
    this.consentState = { ...this.consentState, ...newState };

    // Save to localStorage
    localStorage.setItem('cookieConsent', JSON.stringify(this.consentState));

    // Apply consent changes
    this.applyConsent();

    // Notify listeners
    this.listeners.forEach(listener => listener(this.consentState));

    // Log consent changes for debugging
    if (previousState.analytics !== this.consentState.analytics) {
      console.log(`Analytics consent changed to: ${this.consentState.analytics}`);
    }
  }

  private applyConsent() {
    // Handle Microsoft Clarity
    if (this.consentState.analytics) {
      if (!this.clarityInitialized) {
        this.initializeClarity();
      } else {
        // Re-enable Clarity tracking
        Clarity.consent();
      }
    } else if (this.clarityInitialized) {
      // Disable Clarity tracking
      Clarity.consent(false);
    }

    // Handle Application Insights
    if (this.consentState.analytics) {
      if (!this.appInsightsInitialized) {
        this.initializeAppInsights();
      } else {
        // Re-enable Application Insights
        appInsights.config.disableCookiesUsage = false;
      }
    } else if (this.appInsightsInitialized) {
      // Disable Application Insights cookies and tracking
      appInsights.config.disableCookiesUsage = true;

      // Clear Application Insights cookies
      this.clearAppInsightsCookies();
    }
  }

  private initializeClarity() {
    const clarityId = import.meta.env.VITE_CLARITY_PROJECT_ID || 'tfqdjtkt28';

    if (clarityId && !this.clarityInitialized) {
      Clarity.init(clarityId);
      Clarity.consent(true);
      this.clarityInitialized = true;
      console.log('Microsoft Clarity initialized with consent');
    }
  }

  private initializeAppInsights() {
    if (import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING && !this.appInsightsInitialized) {
      // Application Insights is already initialized in appInsights.ts
      // Just enable cookie usage
      appInsights.config.disableCookiesUsage = false;
      this.appInsightsInitialized = true;
      console.log('Application Insights tracking enabled with consent');
    }
  }

  private clearAppInsightsCookies() {
    // Clear Application Insights related cookies
    const cookiesToClear = ['ai_user', 'ai_session', 'ai_authUser'];
    cookiesToClear.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    });
  }

  public hasConsent(category: ConsentCategory): boolean {
    return this.consentState[category];
  }

  public getConsentState(): ConsentState {
    return { ...this.consentState };
  }

  public subscribe(listener: (state: ConsentState) => void): () => void {
    this.listeners.push(listener);
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Manual consent methods for testing or custom UI
  public grantAnalyticsConsent() {
    this.updateConsent({ analytics: true });
  }

  public revokeAnalyticsConsent() {
    this.updateConsent({ analytics: false });
  }

  public grantAllConsent() {
    this.updateConsent({
      analytics: true,
      marketing: true,
      functional: true,
      necessary: true
    });
  }

  public revokeAllConsent() {
    this.updateConsent({
      analytics: false,
      marketing: false,
      functional: true, // Usually kept as true for basic functionality
      necessary: true  // Always true for necessary cookies
    });
  }
}

// Export singleton instance
export const consentManager = new ConsentManager();

// Export type for use in components
export type { ConsentState, ConsentCategory };